import z from 'zod';

// Matches Huma's ErrorModel (RFC 9457 Problem Details)
const ErrorResponseSchema = z.object({
  detail: z.string().optional(),
  errors: z
    .array(
      z.object({
        message: z.string(),
        location: z.string().optional(),
        value: z.unknown().optional(),
      }),
    )
    .nullable()
    .optional(),
});

export type ErrorResponseBody = z.infer<typeof ErrorResponseSchema>;
export type ApiErrorDetail = NonNullable<NonNullable<ErrorResponseBody['errors']>>[number];

// Custom Error class to represent API HTTP errors.
// Includes the original Request and Response objects for further inspection.
export class ApiHttpError extends Error {
  private constructor(
    public readonly request: Request,
    public readonly response: Response,
    public readonly message: string,
    public readonly detail?: string,
    public readonly errors?: ApiErrorDetail[],
    public readonly responseBody?: unknown,
  ) {
    super(message);
  }

  // Factory method to create an ApiHttpError from a Request and Response.
  // Returns undefined if the response is OK (2xx).
  static async create(request: Request, response: Response) {
    if (!response.ok) {
      let detail: string | undefined = undefined;
      let errors: ApiErrorDetail[] | undefined = undefined;
      let responseBody: unknown = undefined;

      // Try to parse JSON response body, but handle non-JSON responses gracefully
      // We store the parsed body since Response can only be read once
      try {
        responseBody = await response.json();
        const parsed = ErrorResponseSchema.safeParse(responseBody);
        if (parsed.success) {
          detail = parsed.data.detail;
          errors = parsed.data.errors ?? undefined;
        }
      } catch {
        // Response body is not JSON, which is fine - just skip detail extraction
      }

      // HTTP/2 doesn't transmit statusText (always empty in prod over Cloudflare/Fly).
      // Fall back to the parsed `detail` so the message stays useful instead of e.g. "API HTTP Error 422: ".
      const trailer = response.statusText || detail || '';
      const message = trailer
        ? `API HTTP Error ${response.status}: ${trailer}`
        : `API HTTP Error ${response.status}`;

      return new ApiHttpError(request, response, message, detail, errors, responseBody);
    }
  }
}
